"use client";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { X } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { NormalizedVariant } from "../../../../utils/productCard.utils";
import { cn } from "@/app/utils/cn";
import { useMountTransition } from "@/app/hooks/useMountTransition";
import { trackProductClick } from "@/app/lib/tracking/interactions";

interface ProductCardVariantsSheetProps {
  variants: NormalizedVariant[];
  productName: string;
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: () => void;
}

interface SheetContentProps {
  variants: NormalizedVariant[];
  productName: string;
  isVisible: boolean;
  onClose: () => void;
  onNavigate?: () => void;
}

/**
 * Sheet gövdesi — yalnızca açıldığında mount olur. Scroll kilidi effect'i de
 * burada: eskiden kapalı sheet'ler de mount'ta body.style.overflow'a yazıyordu,
 * 84 kart = 84 gereksiz stil geçersizleştirmesi.
 */
const SheetContent = ({
  variants,
  productName,
  isVisible,
  onClose,
  onNavigate,
}: SheetContentProps) => {
  useEffect(() => {
    document.body.style.overflow = isVisible ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isVisible]);

  if (typeof window === "undefined") return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[99998] bg-black/50"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: "opacity 200ms ease",
          pointerEvents: isVisible ? "auto" : "none",
        }}
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[99999] bg-white rounded-t-2xl pb-safe"
        style={{
          transform: isVisible ? "translateY(0)" : "translateY(100%)",
          transition: "transform 300ms ease-out",
          willChange: "transform",
          pointerEvents: isVisible ? "auto" : "none",
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <p className="text-xs text-muted-foreground">
              Renk Seçenekleri
            </p>
            <p className="font-semibold text-sm line-clamp-1">
              {productName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Variants count */}
        <div className="px-4 pt-3 pb-1">
          <p className="text-xs text-muted-foreground">
            {variants.length} farklı renk seçeneği
          </p>
        </div>

        {/* Swiper */}
        <div className="px-4 pb-6">
          <Swiper
            spaceBetween={10}
            slidesPerView={3.5}
            freeMode={true}
            modules={[FreeMode]}
            className="overflow-visible!"
            watchSlidesProgress={false}
            updateOnWindowResize={false}
          >
            {variants.map((variant) => (
              <SwiperSlide key={variant.id}>
                <Link
                  href={`/${variant.slug}`}
                  onClick={() => {
                    trackProductClick(variant.id);
                    onClose();
                    onNavigate?.();
                  }}
                  className="block"
                >
                  <div
                    className={cn(
                      "relative w-full aspect-2/3 rounded-xl overflow-hidden border-2 bg-linear-to-br from-gray-50 to-gray-100",
                      "border-gray-200 active:border-main transition-colors",
                    )}
                  >
                    <FallbackImage
                      src={variant.image}
                      alt={variant.color}
                      fill
                      className="object-cover"
                      fallbackClassName="object-contain scale-75 opacity-80"
                      sizes="120px"
                    />
                    {variant.isPopular && (
                      <div className="absolute top-1.5 left-1.5 bg-main text-white text-[9px] px-1.5 py-0.5 rounded-full font-medium">
                        Popüler
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-center mt-1.5 text-gray-700 font-medium truncate">
                    {variant.color}
                  </p>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </>,
    document.body,
  );
};

/** Dış sarmalayıcı — tek hook: geçiş durumu. Kapalıyken maliyeti neredeyse sıfır. */
const ProductCardVariantsSheet = ({
  variants,
  productName,
  isOpen,
  onClose,
  onNavigate,
}: ProductCardVariantsSheetProps) => {
  const { rendered, isVisible } = useMountTransition(isOpen, 300);

  if (!rendered) return null;

  return (
    <SheetContent
      variants={variants}
      productName={productName}
      isVisible={isVisible}
      onClose={onClose}
      onNavigate={onNavigate}
    />
  );
};

export default ProductCardVariantsSheet;